// src/components/users/UserDetailDrawer.js
// --- UPDATE (User Management) ------------------------------------------------------------
// One account: profile, what the role can do (per module), account security (temporary password,
// last password change, last sign-in when tracked) and the audit trail (who changed what).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton, Avatar, Box, HStack, VStack, Text, Badge,
    SimpleGrid, Button, Icon, Tabs, TabList, Tab, TabPanels, TabPanel, Skeleton, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { MdEdit, MdLockReset, MdBlock, MdCheckCircle, MdHistory, MdSecurity, MdPerson } from 'react-icons/md';
import { roleMeta } from '../../config/roles';
import { explainRole } from './userAiIntel';
import userAdminApi from '../../services/userAdminApi';

const STATUS = { active: { label: 'Active', scheme: 'green' }, inactive: { label: 'Inactive', scheme: 'gray' }, on_leave: { label: 'On leave', scheme: 'orange' } };
const ACTION_LABEL = { created: 'Account created', profile_updated: 'Profile updated', role_changed: 'Role changed', status_changed: 'Status changed', password_reset: 'Password reset', password_changed: 'Changed own password', deleted: 'Deleted' };
const fmt = (d) => { if (!d) return '—'; const t = new Date(d); return Number.isFinite(t.getTime()) ? t.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'; };

const Fact = ({ label, value, muted }) => (
    <Box><Text fontSize="2xs" textTransform="uppercase" fontWeight="700" color={muted}>{label}</Text><Text fontSize="sm" fontWeight="600" wordBreak="break-word">{value || '—'}</Text></Box>
);
Fact.propTypes = { label: PropTypes.string, value: PropTypes.node, muted: PropTypes.string };

const describeChange = (a) => {
    const d = a.details;
    if (!d || typeof d !== 'object') return null;
    if (a.action === 'created') return `Role ${roleMeta(d.role).label}${d.generated_password ? ' · temporary password generated' : ''}`;
    if (a.action === 'password_reset') return `${d.generated ? 'Generated' : 'Typed'} password${d.must_change_password ? ' · must change at next sign-in' : ''}`;
    return Object.entries(d).map(([k, v]) => (v && typeof v === 'object' && 'to' in v ? `${k.replace(/_/g, ' ')}: ${k === 'role' ? `${roleMeta(v.from).label} → ${roleMeta(v.to).label}` : `${v.from ?? '—'} → ${v.to ?? '—'}`}` : null)).filter(Boolean).join(' · ');
};

const UserDetailDrawer = ({ isOpen, onClose, user, canManage, onEdit, onReset, onToggleStatus }) => {
    const [audit, setAudit] = useState(null);
    const muted = useColorModeValue('gray.500', 'gray.400');
    const tile = useColorModeValue('gray.50', 'whiteAlpha.50');
    useEffect(() => {
        let alive = true;
        if (!isOpen || !user?.id) return undefined;
        setAudit(null);
        userAdminApi.audit(user.id).then((a) => { if (alive) setAudit(Array.isArray(a) ? a : []); }).catch(() => { if (alive) setAudit([]); });
        return () => { alive = false; };
    }, [isOpen, user?.id]);
    if (!user) return null;
    const m = roleMeta(user.role);
    const info = explainRole(user.role);
    const st = STATUS[user.status] || STATUS.active;

    return (
        <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">
                    <HStack spacing={3}>
                        <Avatar name={user.display_name} bg={`${m.color}.500`} color="white" />
                        <Box>
                            <Text fontWeight="800">{user.display_name}</Text>
                            <HStack spacing={1} mt={1} wrap="wrap">
                                <Badge colorScheme={m.color}>{m.label}</Badge>
                                <Badge colorScheme={st.scheme} variant="subtle">{st.label}</Badge>
                                {user.must_change_password && <Badge colorScheme="yellow" variant="outline">Temporary password</Badge>}
                                {user.is_self && <Badge colorScheme="purple">You</Badge>}
                            </HStack>
                        </Box>
                    </HStack>
                </DrawerHeader>
                <DrawerBody>
                    <Tabs size="sm" colorScheme="purple" isLazy>
                        <TabList>
                            <Tab><Icon as={MdPerson} mr={1} />Profile</Tab>
                            <Tab><Icon as={MdSecurity} mr={1} />Permissions</Tab>
                            <Tab><Icon as={MdHistory} mr={1} />Activity</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel px={0}>
                                <SimpleGrid columns={2} spacing={3} bg={tile} p={3} borderRadius="lg">
                                    <Fact label="Username" value={`@${user.username}`} muted={muted} />
                                    <Fact label="Email" value={user.email} muted={muted} />
                                    <Fact label="Phone" value={user.phone} muted={muted} />
                                    <Fact label="Employee no." value={user.employee_id} muted={muted} />
                                    <Fact label="Department" value={user.department} muted={muted} />
                                    <Fact label="Job title" value={user.job_title} muted={muted} />
                                    <Fact label="Open work orders" value={user.open_work_orders ?? '—'} muted={muted} />
                                    <Fact label="Skills" value={(user.skills || []).length ? user.skills.join(', ') : null} muted={muted} />
                                    <Fact label="Created" value={fmt(user.created_at)} muted={muted} />
                                    <Fact label="Last updated" value={fmt(user.updated_at)} muted={muted} />
                                    <Fact label="Password changed" value={fmt(user.password_changed_at)} muted={muted} />
                                    <Fact label="Last sign-in" value={user.last_login_at ? fmt(user.last_login_at) : 'not tracked yet'} muted={muted} />
                                </SimpleGrid>
                                {user.notes && <Text fontSize="sm" mt={3} whiteSpace="pre-wrap"><b>Notes:</b> {user.notes}</Text>}
                            </TabPanel>
                            <TabPanel px={0}>
                                <Text fontSize="sm" mb={3}>{info.summary}</Text>
                                <VStack align="stretch" spacing={1}>
                                    {info.modules.map((mod) => (
                                        <HStack key={mod.key} justify="space-between" p={2} bg={tile} borderRadius="md">
                                            <Text fontSize="sm">{mod.label}</Text>
                                            <Tooltip hasArrow label={mod.info?.explain}><Badge colorScheme={mod.info?.color || 'gray'} cursor="help">{mod.info?.label || '—'}</Badge></Tooltip>
                                        </HStack>
                                    ))}
                                </VStack>
                            </TabPanel>
                            <TabPanel px={0}>
                                {audit === null ? <Skeleton h="80px" borderRadius="lg" /> : audit.length === 0 ? <Text fontSize="sm" color={muted}>No recorded changes yet — changes made from this page are logged from now on.</Text> : (
                                    <VStack align="stretch" spacing={2}>
                                        {audit.map((a) => (
                                            <Box key={a.id} p={2} borderLeftWidth="3px" borderLeftColor="purple.300" bg={tile} borderRadius="md">
                                                <HStack justify="space-between"><Text fontSize="sm" fontWeight="700">{ACTION_LABEL[a.action] || a.action}</Text><Text fontSize="2xs" color={muted}>{fmt(a.created_at)}</Text></HStack>
                                                <Text fontSize="xs" color={muted}>{describeChange(a) || ''}{a.actor_name ? `${describeChange(a) ? ' · ' : ''}by ${a.actor_name}` : ''}</Text>
                                            </Box>
                                        ))}
                                    </VStack>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </DrawerBody>
                {canManage && (
                    <DrawerFooter borderTopWidth="1px" gap={2} flexWrap="wrap">
                        <Button size="sm" leftIcon={<Icon as={MdEdit} />} onClick={() => onEdit(user)}>Edit</Button>
                        {!user.is_self && <Button size="sm" leftIcon={<Icon as={MdLockReset} />} onClick={() => onReset(user)}>Reset password</Button>}
                        {!user.is_self && (
                            <Button size="sm" colorScheme={user.status === 'active' ? 'red' : 'green'} variant="outline" leftIcon={<Icon as={user.status === 'active' ? MdBlock : MdCheckCircle} />} onClick={() => onToggleStatus(user)}>
                                {user.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                        )}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
};

UserDetailDrawer.propTypes = { isOpen: PropTypes.bool, onClose: PropTypes.func.isRequired, user: PropTypes.object, canManage: PropTypes.bool, onEdit: PropTypes.func, onReset: PropTypes.func, onToggleStatus: PropTypes.func };

export default UserDetailDrawer;
